// Web Push Notification Service
import { api } from './api';

class PushNotificationService {
  constructor() {
    this.permission = 'default';
    this.swRegistration = null;
  }

  // Check if push notifications are supported
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        await this.subscribeToServerNotifications();
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }

  // Subscribe to push notifications from server
  async subscribeToServerNotifications() {
    try {
      // Register service worker if not already
      if (!this.swRegistration) {
        this.swRegistration = await navigator.serviceWorker.ready;
      }

      // For web push, we'll use a different approach - polling or WebSocket
      // Since we don't have VAPID keys set up, we'll use local notifications
      console.log('Push notification subscription ready');
      
      return true;
    } catch (err) {
      console.error('Error subscribing to notifications:', err);
      return false;
    }
  }

  // Show a local notification
  async showNotification(title, options = {}) {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      return false;
    }

    const defaultOptions = {
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      vibrate: [200, 100, 200],
      tag: 'security-guard-notification',
      renotify: true,
      requireInteraction: false,
      ...options
    };

    try {
      if (this.swRegistration) {
        await this.swRegistration.showNotification(title, defaultOptions);
      } else {
        new Notification(title, defaultOptions);
      }
      return true;
    } catch (err) {
      console.error('Error showing notification:', err);
      return false;
    }
  }

  // Show incident notification
  async showIncidentNotification(incident) {
    const severityEmoji = {
      low: '🟢',
      medium: '🟡',
      high: '🟠',
      critical: '🔴'
    };

    return this.showNotification(
      `${severityEmoji[incident.severity] || '⚠️'} New Incident: ${incident.title}`,
      {
        body: incident.description?.substring(0, 100) + '...',
        tag: `incident-${incident.id}`,
        data: { type: 'incident', id: incident.id },
        actions: [
          { action: 'view', title: 'View Details' },
          { action: 'dismiss', title: 'Dismiss' }
        ]
      }
    );
  }

  // Show shift reminder notification
  async showShiftReminder(shift) {
    return this.showNotification(
      '⏰ Shift Reminder',
      {
        body: `Your shift at ${shift.site_name} starts in 30 minutes (${shift.start_time})`,
        tag: `shift-${shift.id}`,
        data: { type: 'shift', id: shift.id }
      }
    );
  }

  // Show checkpoint missed notification
  async showCheckpointMissed(checkpoint) {
    return this.showNotification(
      '📍 Checkpoint Missed',
      {
        body: `You haven't scanned ${checkpoint.name} yet. Please complete your patrol.`,
        tag: `checkpoint-${checkpoint.id}`,
        data: { type: 'checkpoint', id: checkpoint.id }
      }
    );
  }

  // Show clock out reminder
  async showClockOutReminder() {
    return this.showNotification(
      '🕐 Shift Ending Soon',
      {
        body: 'Your shift is ending in 15 minutes. Don\'t forget to clock out!',
        tag: 'clock-out-reminder'
      }
    );
  }

  // Get permission status
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }
}

export const pushService = new PushNotificationService();
export default pushService;
